import {
  SkillForm,
  type SkillFormValues,
  type SkillSubmitResult,
} from "~/components/assistant/SkillForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { DEFAULT_SKILL_CONTENT } from "~/services/skills";

export type { SkillFormValues, SkillSubmitResult };

export const SkillFormDialogMode = {
  CREATE: "create",
  EDIT: "edit",
} as const;
export type SkillFormDialogMode = (typeof SkillFormDialogMode)[keyof typeof SkillFormDialogMode];

interface SkillFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: SkillFormValues) => Promise<SkillSubmitResult>;
  mode?: SkillFormDialogMode;
  initialData?: Partial<SkillFormValues>;
  duplicateSourceName?: string;
  description?: string;
  readOnly?: boolean;
}

export const SkillFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode = SkillFormDialogMode.CREATE,
  initialData,
  duplicateSourceName,
  description,
  readOnly = false,
}: SkillFormDialogProps) => {
  const isDuplicate =
    !readOnly && mode !== SkillFormDialogMode.EDIT && Boolean(duplicateSourceName);
  const title = readOnly ? (
    "Skill Details"
  ) : mode === SkillFormDialogMode.EDIT ? (
    "Edit Skill"
  ) : isDuplicate ? (
    <>
      Duplicate <span className="text-muted-foreground">·</span> <span>{duplicateSourceName}</span>
    </>
  ) : (
    "Add New Skill"
  );
  const shownDescription = readOnly || mode === SkillFormDialogMode.EDIT ? undefined : description;
  const saveLabel = mode === SkillFormDialogMode.EDIT ? "Save" : isDuplicate ? "Add copy" : "Add";

  const initial: SkillFormValues = {
    name: initialData?.name ?? "",
    description: initialData?.description ?? "",
    content: initialData?.content ?? DEFAULT_SKILL_CONTENT,
  };

  // Remounts SkillForm whenever the dialog closes or targets a different
  // skill, so stale field values and touched-state never leak between uses.
  const formKey = !open
    ? "closed"
    : `${mode}:${readOnly}:${duplicateSourceName ?? ""}:${initial.name}`;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 text-foreground sm:w-[70vw] sm:max-w-[920px]"
        onOpenAutoFocus={readOnly ? (event) => event.preventDefault() : undefined}
        {...(!shownDescription && { "aria-describedby": undefined })}
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>{title}</DialogTitle>
          {shownDescription && <DialogDescription>{shownDescription}</DialogDescription>}
        </DialogHeader>
        <SkillForm
          key={formKey}
          initial={initial}
          saveLabel={saveLabel}
          onSubmit={onSubmit}
          onCancel={onClose}
          readOnly={readOnly}
        />
      </DialogContent>
    </Dialog>
  );
};
